import './CopilotCustomPrompts.css';
import { observer } from 'mobx-react-lite';
import { Typography } from '@material-ui/core';
import React, { useEffect } from 'react';
import { Divider, Stack } from '@mui/material';
import { CopilotTab, CopilotTabs } from '../../components/CopilotTabs';
import { CopilotInputItems, InputItem } from '../../components/CopilotInputItems';
import { PrimaryButton } from '../../../controls/buttons';
import CopilotTooltip from '../../components/CopilotTooltip';
import DeleteDialog from './dialogs/DeleteDialog';
import { CustomPromptScope } from '../../types';
import ModificationsDialog from './dialogs/ModificationsDialog';
import { TestPromptTab } from './TestPromptTab';
import { PreviewPromptTab } from './PreviewPromptTab';
import { CopilotStore } from '../../CopilotStore';


type CopilotCustomPromptsProps = {
    store: CopilotStore
}

export const CopilotCustomPrompts = observer(({store}: CopilotCustomPromptsProps) => {
    const {
        models,
        tagValuesLoading,
        selectedCustomer: customer,
        selectedModel,
        setSelectedModel,
        getAllPrompts,
        systemPrompt,
        backend,
        customPrompts,
        getAllModelsWithPrompt
    } = store

    const [promptTitle, setPromptTitle] = React.useState<string>("");
    const [promptText, setPromptText] = React.useState<string>("");
    const [promptScope, setPromptScope] = React.useState<CustomPromptScope>(selectedModel ? 'tag' : 'customer');
    const [newPrompt, setNewPromot] = React.useState<boolean>(true);
    const [processingStatus, setProcessingStatus] = React.useState<boolean>(false)

    const [customPromptValid, setCustomPromptValid] = React.useState<boolean>(false);
    const [customPromptTested, setCustomPromptTested] = React.useState<boolean>(false);
    const [currentTab, setCurrentTab] = React.useState<number>(0);

    // dialogs
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
    const [createDialogOpen, setCreateDialogOpen] = React.useState<boolean>(false);

    useEffect(() => {}, [customer, selectedModel])

    useEffect(() => {
        let selectedPrompt = undefined
        if (promptScope === 'customer') {
            selectedPrompt = customPrompts.find(prompt => prompt.prompt_scope === 'customer')
        }
        else selectedPrompt = customPrompts.find(prompt => prompt.prompt_scope === 'tag' && prompt.tag_id === selectedModel?.value)

        setNewPromot(selectedPrompt === undefined)
        setPromptTitle(selectedPrompt?.prompt_title ?? '')
        setPromptText(selectedPrompt?.prompt ?? '')

    }, [customPrompts, promptScope, selectedModel, customer])


    const applyToItems: InputItem[] = [
        {
            type: 'radiogroup',
            label: 'Apply to:',
            onChange: (event?: any) =>{
                setSelectedModel(undefined)
                setPromptScope(event.target.value)
                setCustomPromptTested(false)
                setCustomPromptValid(false)
            },
            items: [
                {value: 'customer' , label: 'Customer level (All models)'},
                {value: 'tag', label: 'Specific model'}
            ],
            selectedItem: promptScope,
            value: promptScope,
            isReady: ()=>true,
            showLoading: ()=>false,
            disabled: currentTab === 1
        },
        {
            type: 'select',
            label: '',
            onChange: (item: any) => {
                setSelectedModel(item?.value)
                setCustomPromptTested(false)
                setCustomPromptValid(false)
            },
            items: models.map(tc => ({ value: tc.rowid, label: tc.value })),
            selectedItem: selectedModel?.rowid || '',
            isReady: () => true,
            showLoading: () => tagValuesLoading,
            disabled: currentTab === 1 || promptScope !== 'tag'
        }
    ]

    const inputItems: InputItem[] = [
        {
            type: 'input',
            label: 'Title',
            onChange: (event?: any) =>setPromptTitle(event.target.value),
            value: promptTitle,
            isReady: ()=>true,
            showLoading: ()=>false,
            disabled: currentTab === 1
        },
        {
            type: 'multiline',
            label: 'Prompt text',
            onChange: (event?: any)=>{
                setPromptText(event.target.value)
                setCustomPromptTested(false)
                setCustomPromptValid(false)
            },
            value: promptText,
            isReady: ()=>true,
            showLoading: ()=>false,
            disabled: currentTab === 1,
            placeholder: 'Write custom prompt'
        },
    ]

    const tabs: CopilotTab[]  = [
        {
            label: 'Preview prompt',
            component: <PreviewPromptTab
                        systemPrompt={systemPrompt}
                        promptText={promptText} />
        },
        {
            label: 'Test prompt',
            component: <TestPromptTab
                store={store}
                promptText={promptText}
                customPromptValid={customPromptValid}
                setCustomPromptValid={setCustomPromptValid}
                customPromptTested={customPromptTested}
                setCustomPromptTested={setCustomPromptTested}
                processingStatus={processingStatus}
                setProcessingStatus={setProcessingStatus}
                selectedPromptModel={selectedModel}
                promptScope={promptScope}
                />
        },
    ]

    const createPromptTooltip = () => {
        if(!customPromptTested) return 'You have to test your prompt first in order to save it.'
        else return "The test result must be validated before creating a new custom prompt."
    }

    const createPromptEnabled = () => {
        return customPromptTested && customPromptValid && !processingStatus
    }

    const upsertCustomPromptRequest = async () => {
        setProcessingStatus(true)
        await backend?.upsertCustomPrompt(promptScope,
                                          customer?.name || '',
                                          promptText,
                                          promptTitle,
                                          promptScope==='tag'? selectedModel?.value: undefined)
        setProcessingStatus(false)
        getAllPrompts(customer?.name || '', true)
    }

    const deleteCustomPromptRequest = async () => {
        setProcessingStatus(true)
        await backend?.deleteCustomPrompt(promptScope,
                                          customer?.name || '',
                                          promptScope==='tag'? selectedModel?.value: undefined)
        setProcessingStatus(false)
        getAllPrompts(customer?.name || '', true)
    }

    const handleSaveCreateClick = () => {
        if(promptScope === 'customer' && getAllModelsWithPrompt().length === 0){
            upsertCustomPromptRequest()
        } else {
            setCreateDialogOpen(true)
        }

    }

    const renderCreateDialog = () => {
        let text = ''
        let alertText = ''
        let children = undefined

        if(promptScope === 'customer'){
            const modelsWithPrompt = getAllModelsWithPrompt()
            alertText = `Please note that this change will not apply to all models. `
            text = `Certain prompts have been modified for specific models, and as a result, these models are no longer linked to the customer prompt. Therefore, they will not be influenced by the customer's custom prompt.`
            if(modelsWithPrompt) {
                children = <Stack>
                    <Typography className='ModelsTitle'>The following models will not be affected by this change</Typography>
                    <Stack maxHeight='270px' style={{overflowY: 'auto'}}>
                        {modelsWithPrompt.map((model, i) =><Typography key={i} className='ModelName'>{model}</Typography>)}
                    </Stack>
                </Stack>
            }
        } else { // model level
            alertText = `Please note that once a specific model is modified, it will no longer be linked to the customer prompt (if available) and will not be able to inherit any further changes.`
            text = `As long as no changes have been made to a specific model, it will remain linked to the customer prompt and will be subject to changes made at the customer level.`
        }

        return <ModificationsDialog
                    open={createDialogOpen}
                    onClose={()=>setCreateDialogOpen(false)}
                    onSubmit={()=>upsertCustomPromptRequest()}
                    text={text}
                    alertText={alertText}>
                        {children}
                </ModificationsDialog>
    }

    return (<>
        <Stack direction={'row'} spacing={6} height='100%' padding={2}>
            <Stack flex={1} spacing={3}>
                <CopilotInputItems inputItems={applyToItems}/>
                <Divider/>
                <CopilotInputItems inputItems={inputItems}/>
                <Stack justifySelf='flex-end' flexDirection={'row'}>
                    {!newPrompt && <PrimaryButton
                                        onClick={()=>setDeleteDialogOpen(true)}
                                        style={{display:'flex', height: '40px', width: '116px', color: 'white',backgroundColor:  "#FF6262", marginRight: '20px'}}>
                                            Delete
                                            </PrimaryButton>}
                    <CopilotTooltip
                        placement='left'
                        title={createPromptTooltip()}
                        disableHoverListener={createPromptEnabled() /* TODO - should be false when disabled */}>
                        <Stack >
                            <PrimaryButton
                                disabled={!createPromptEnabled()}
                                onClick={()=>handleSaveCreateClick()}
                                style={{display:'flex', height: '40px', width: '116px', color: 'white',backgroundColor: !createPromptEnabled() ? '#769ffb' : '#2468FF' }}>
                                    {!newPrompt? "Save": "Create Prompt"}
                            </PrimaryButton>
                        </Stack>
                    </CopilotTooltip>
                </Stack>
            </Stack>
            <Stack flex={1}>
                <CopilotTabs tabs={tabs} setCurrentTab={setCurrentTab}/>
            </Stack>
        </Stack>

        <DeleteDialog open={deleteDialogOpen} onClose={()=>setDeleteDialogOpen(false)} onSubmit={()=>deleteCustomPromptRequest()} />
        {createDialogOpen && renderCreateDialog()}
    </>)
})